// Platform standard: money and rates always render to exactly this many
// decimals. Counts stay whole, and so do compact chart axis ticks.
export const DISPLAY_DECIMALS = 2;

// Compact number abbreviation (K / M / B), trimming trailing ".0" ("1.0K" → "1K").
// Returns "—" for null / NaN / non-positive values (the "no data" placeholder).
export function formatCompact(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return '—';
  const abs = Math.abs(v);
  // Thresholds sit where two-decimal rounding would read "1000.00" of the unit below (999,995 → "1M", never "1000K").
  if (abs >= 999_995_000) return trimZero((v / 1_000_000_000).toFixed(2)) + 'B';
  if (abs >= 999_995) return trimZero((v / 1_000_000).toFixed(2)) + 'M';
  if (abs >= 999.995) return trimZero((v / 1_000).toFixed(2)) + 'K';
  return v.toFixed(2).replace(/\.?0+$/, '');
}

// Three-letter months (the en-GB locale would print "Sept").
export const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// One-decimal compact count for dense dashboard cells ("16.3K", "2.4M", "680"); whole
// units from 100K / 100M. A value that rounds up to the next unit rolls over ("1M", not "1000K").
export function formatCompactShort(n: number): string {
  const unit = (v: number, d: number) => v.toFixed(d).replace(/\.0$/, '');
  const m = unit(n / 1e6, n >= 1e8 ? 0 : 1);
  const k = unit(n / 1e3, n >= 1e5 ? 0 : 1);
  if (n >= 1e6 || k === '1000') return `${m}M`;
  if (n >= 1e3 || Math.round(n) === 1000) return `${k}K`;
  return String(Math.round(n));
}

// Creator-tier range display: "10000-50000" → "10K – 50K". Falls back to the raw
// string for anything that isn't a clean "min-max" number pair.
export function formatTierRange(value: string): string {
  const parts = value.split('-');
  if (parts.length !== 2) return value;
  const min = Number(parts[0]);
  const max = Number(parts[1]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return value;
  return `${formatCompact(min)} – ${formatCompact(max)}`;
}

// Indian rupee formatting, switching to Lakh (L) / Crore (Cr) at 1e5 / 1e7 and
// Indian comma grouping below 1L. Always two decimals, abbreviated units
// included — money never rounds to a whole unit (platform standard).
export function formatCurrencyINR(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  // Sign leads the symbol, matching the Intl output formatMoneyExact returns below.
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(DISPLAY_DECIMALS)} Cr`;
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(DISPLAY_DECIMALS)} L`;
  return formatMoneyExact(v);
}

const INR_EXACT = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: DISPLAY_DECIMALS,
  maximumFractionDigits: DISPLAY_DECIMALS,
});

// Exact rupee amount: Indian grouping, always 2 decimals, never abbreviated (contrast
// formatCurrencyINR).
export function formatMoneyExact(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return INR_EXACT.format(v);
}

const MONEY_SHRINK_STEPS: ReadonlyArray<{ minLength: number; className: string }> = [
  { minLength: 16, className: 'text-[11px]' },
  { minLength: 14, className: 'text-[12px]' },
];

// Font-size class shrinking long exact amounts to fit a fixed-width cell; '' keeps the
// inherited size.
export function moneyFitClass(formatted: string): string {
  return MONEY_SHRINK_STEPS.find((s) => formatted.length >= s.minLength)?.className ?? '';
}

const CPV_CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹' };

// Cost-per-view display, always two decimals ("₹0.09"); 0 / missing → "—".
// An unmapped currency shows its ISO code so a non-INR value isn't mislabelled as
// rupees; no currency → INR.
export function formatCpv(v: number | null | undefined, currency?: string | null): string {
  if (v == null || !Number.isFinite(v) || v <= 0) return '—';
  const code = currency || 'INR';
  const symbol = CPV_CURRENCY_SYMBOLS[code] ?? `${code} `;
  return `${symbol}${v.toFixed(DISPLAY_DECIMALS)}`;
}

// Indian-grouped integer formatter for the rare callsite that needs raw counts
// without K/M/B abbreviation.
export function formatIntegerIN(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-IN').format(v);
}

const BYTES_PER_KB = 1024;
const BYTE_LABEL = 'B';
// Largest first: a size takes the first unit it reaches. Binary multiples under
// the familiar labels, matching what the "10 MB" upload limit means (10 MiB).
const BYTE_UNITS = [
  { label: 'GB', bytes: BYTES_PER_KB ** 3 },
  { label: 'MB', bytes: BYTES_PER_KB ** 2 },
  { label: 'KB', bytes: BYTES_PER_KB },
] as const;
// Below 10 of a unit a size keeps one decimal ("2.4 MB"); from 10 up, whole ("512 KB").
const ONE_DECIMAL_BELOW = 10;
const ONE_DECIMAL_SCALE = 10;

// File size for display: "900 B", "2 KB", "2.4 MB", "512 KB".
export function formatBytes(bytes: number): string {
  const unit = BYTE_UNITS.find((u) => bytes >= u.bytes);
  if (!unit) return `${bytes} ${BYTE_LABEL}`;
  return `${roundForSize(bytes / unit.bytes)} ${unit.label}`;
}

function roundForSize(value: number): number {
  if (value >= ONE_DECIMAL_BELOW) return Math.round(value);
  return Math.round(value * ONE_DECIMAL_SCALE) / ONE_DECIMAL_SCALE;
}

const ELLIPSIS = '…';

// Shortens `text` to `maxChars` by cutting its middle, so the start and the end
// (a file name's extension) both stay readable: "quarterly-camp…tes-final.pdf".
export function middleTruncate(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  const kept = maxChars - ELLIPSIS.length;
  const headLength = Math.ceil(kept / 2);
  const tailLength = kept - headLength;
  return `${text.slice(0, headLength)}${ELLIPSIS}${text.slice(text.length - tailLength)}`;
}

function trimZero(s: string): string {
  // Remove trailing zeros after decimal: "1.50" → "1.5", "1.00" → "1", "1.20" → "1.2"
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}
